import { useEffect, useState } from 'react'
import {
  Button,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BankOutlined, PlusOutlined, TagsOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { applyApiFieldErrors, isHandledAuthError, municipalitiesApi } from '@/services'
import { officeReviewStatusesApi, type OfficeReviewStatusAdmin } from '@/services/officeReviewStatuses'
import { invalidateOfficeReviewStatusesCache } from '@/hooks/useOfficeReviewStatuses'
import { usePermission } from '@/hooks/usePermission'
import { isValidHexColor, readableTextColor } from '@/utils/color'
import { useToast } from '@/widgets/common'

type FormValues = {
  displayName: string
  color: string
  sortOrder?: number
  isActive: boolean
}

type MunicipalityOption = { id: number; name: string; code: string }

function HexColorField({ value, onChange }: { value?: string; onChange?: (v: string) => void }) {
  return (
    <Space>
      <ColorPicker value={value} onChange={(c) => onChange?.(c.toHexString())} disabledAlpha />
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        style={{ width: 140, fontFamily: 'monospace' }}
        placeholder="#1677ff"
        maxLength={7}
      />
    </Space>
  )
}

/**
 * 事務局確認用ステータス管理（独立画面。顧客要望 管理No.25）。
 *
 * 「ユーザ管理」と同様、自治体ごとに候補値を追加/編集/無効化する。
 * - ADMIN: ログイン自治体スコープで【自分の自治体】を維護（/api/admin/office-review-statuses）。
 * - SYSTEM_ADMIN: 自治体に属さないため、上部の「自治体」選択で対象を選び、その自治体を維護する
 *   （/api/municipalities/{id}/office-review-statuses 経由。SYSTEM_ADMIN は MUNICIPALITY_MGMT を持つ）。
 * ユーザ管理と同じ USER_MGMT 権限で保護（新規権限は追加しない）。
 */
export default function OfficeReviewStatusMgmt() {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { isSystemAdmin } = usePermission()
  const [municipalities, setMunicipalities] = useState<MunicipalityOption[]>([])
  const [targetMunicipalityId, setTargetMunicipalityId] = useState<number | null>(null)
  const [rows, setRows] = useState<OfficeReviewStatusAdmin[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<OfficeReviewStatusAdmin | null>(null)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<FormValues>()

  // SYSTEM_ADMIN は対象自治体を選ぶ必要があるため自治体一覧を取得する。
  useEffect(() => {
    if (!isSystemAdmin) return
    municipalitiesApi
      .list()
      .then(({ data }) => setMunicipalities(data))
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('自治体一覧の取得に失敗しました')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin])

  // SYSTEM_ADMIN なら未選択時は空。選択中の自治体は path 固定 API、ADMIN は自分の自治体 API。
  const needsSelection = isSystemAdmin && targetMunicipalityId == null

  const load = () => {
    if (needsSelection) {
      setRows([])
      return
    }
    setLoading(true)
    const req = isSystemAdmin
      ? officeReviewStatusesApi.listForManagement(targetMunicipalityId as number)
      : officeReviewStatusesApi.listForMyMunicipality()
    req
      .then(({ data }) => setRows(data ?? []))
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('事務局確認用ステータスの取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin, targetMunicipalityId])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ color: '#1677ff', isActive: true })
    setModalOpen(true)
  }

  const openEdit = (row: OfficeReviewStatusAdmin) => {
    setEditing(row)
    form.setFieldsValue({
      displayName: row.displayName,
      color: row.color,
      sortOrder: row.sortOrder,
      isActive: row.isActive,
    })
    setModalOpen(true)
  }

  const submit = async () => {
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    const body = {
      displayName: values.displayName.trim(),
      color: values.color,
      sortOrder: values.sortOrder,
      isActive: values.isActive,
    }
    try {
      if (editing) {
        if (isSystemAdmin) {
          await officeReviewStatusesApi.update(targetMunicipalityId as number, editing.id, body)
        } else {
          await officeReviewStatusesApi.updateForMyMunicipality(editing.id, body)
        }
      } else {
        if (isSystemAdmin) {
          await officeReviewStatusesApi.create(targetMunicipalityId as number, body)
        } else {
          await officeReviewStatusesApi.createForMyMunicipality(body)
        }
      }
      invalidateOfficeReviewStatusesCache()
      toast.success(editing ? 'ステータスを更新しました' : 'ステータスを追加しました')
      setModalOpen(false)
      load()
    } catch (error) {
      if (isHandledAuthError(error)) return
      applyApiFieldErrors(form, error)
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async (row: OfficeReviewStatusAdmin) => {
    try {
      if (isSystemAdmin) {
        await officeReviewStatusesApi.deactivate(targetMunicipalityId as number, row.id)
      } else {
        await officeReviewStatusesApi.deactivateForMyMunicipality(row.id)
      }
      invalidateOfficeReviewStatusesCache()
      toast.success('ステータスを無効化しました')
      load()
    } catch (error) {
      if (!isHandledAuthError(error)) toast.error('無効化に失敗しました')
    }
  }

  const columns: ColumnsType<OfficeReviewStatusAdmin> = [
    {
      title: '表示',
      dataIndex: 'displayName',
      render: (_, row) => {
        const bg = isValidHexColor(row.color) ? row.color : '#d9d9d9'
        return (
          <Tag style={{ backgroundColor: bg, color: readableTextColor(bg), border: 'none', margin: 0 }}>
            {row.displayName}
          </Tag>
        )
      },
    },
    {
      title: '色',
      dataIndex: 'color',
      width: 130,
      render: (color: string) => (
        <Space size={6}>
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 16,
              borderRadius: 4,
              background: color,
              border: '1px solid #d9d9d9',
            }}
          />
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{color}</span>
        </Space>
      ),
    },
    { title: '並び順', dataIndex: 'sortOrder', width: 90 },
    {
      title: '状態',
      dataIndex: 'isActive',
      width: 90,
      render: (active: boolean) => (active ? <Tag color="green">有効</Tag> : <Tag>無効</Tag>),
    },
    {
      title: '操作',
      width: 170,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            編集
          </Button>
          {row.isActive ? (
            <Popconfirm
              title="無効化しますか？"
              description="受給者に設定済みでも参照は保持されます。"
              okText="無効化"
              cancelText="キャンセル"
              okButtonProps={{ danger: true }}
              onConfirm={() => void deactivate(row)}
            >
              <Button size="small" danger>
                無効化
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: '事務局確認用ステータス管理' },
        ]}
      />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <BackButton onClick={() => navigate('submenu-admin')} />

        {/* ヘッダーカード：アイコンバッジ + タイトル + 説明 + 追加 + 自治体ツールバー */}
        <div className="mt-2 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="flex flex-wrap items-start justify-between gap-4 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-md shadow-rose-200">
                <TagsOutlined style={{ fontSize: 22 }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">事務局確認用ステータス管理</h1>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-500">
                  案件ステータスとは別に、事務局内の確認・整理用に手動設定する分類です（受給者一覧で選択）。自治体ごとに候補・色を維護します。
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                  <span>初期値は「-（未設定）」</span>
                  <span>「無効化」は新規選択肢から除外（既存の参照は保持）</span>
                </div>
              </div>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              disabled={needsSelection}
              className="shrink-0"
            >
              ステータスを追加
            </Button>
          </div>

          {isSystemAdmin ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3">
              <BankOutlined className="text-gray-400" />
              <span className="text-sm font-medium text-gray-600">対象自治体</span>
              <Select<number>
                style={{ minWidth: 300 }}
                placeholder="維護する自治体を選択"
                value={targetMunicipalityId ?? undefined}
                onChange={(v) => setTargetMunicipalityId(v ?? null)}
                showSearch
                optionFilterProp="label"
                options={municipalities.map((m) => ({ value: m.id, label: `${m.name}（${m.code}）` }))}
              />
              {!needsSelection ? (
                <span className="ml-auto inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                  全 {rows.length} 件
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* コンテンツカード：空状態 or テーブル */}
        <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          {needsSelection ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                <BankOutlined style={{ fontSize: 28 }} />
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-700">維護する自治体を選択してください</p>
              <p className="mt-1 max-w-md text-xs leading-relaxed text-gray-400">
                システム管理者は自治体に属さないため、上の「対象自治体」から選ぶと、その自治体の事務局確認用ステータスを管理できます。
              </p>
            </div>
          ) : (
            <Table
              rowKey="id"
              size="middle"
              loading={loading}
              columns={columns}
              dataSource={rows}
              pagination={false}
            />
          )}
        </div>
      </div>

      <Modal
        title={editing ? '事務局確認用ステータス 編集' : '事務局確認用ステータス 追加'}
        open={modalOpen}
        onOk={() => void submit()}
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        okText="保存"
        cancelText="キャンセル"
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-2">
          <Form.Item
            name="displayName"
            label="表示名"
            rules={[
              { required: true, message: '表示名は必須です' },
              { max: 64, message: '表示名は64文字以内で入力してください' },
            ]}
          >
            <Input maxLength={64} placeholder="作業中" />
          </Form.Item>
          <Form.Item
            name="color"
            label="色"
            rules={[
              { required: true, message: '色は必須です' },
              { pattern: /^#[0-9a-fA-F]{6}$/, message: '色は #RRGGBB 形式で入力してください' },
            ]}
          >
            <HexColorField />
          </Form.Item>
          <Form.Item name="sortOrder" label="並び順" tooltip="未指定なら末尾に追加します">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="末尾に追加" />
          </Form.Item>
          <Form.Item name="isActive" label="有効" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
